<template>
    <div class="map-icons">
        <div v-if="isCoordsOpen" class="coordinate-display">
            <span>Geográficas (DD): {{ pos.lon }}, {{ pos.lat }}</span>
            <br />
            <span>UTM ({{ utm.zone }}): E: {{ utm.easting }}, N: {{ utm.northing }}</span>
        </div>
        <div style="display: flex; gap: 10px">
            <a-button
                class="map-icon-btn"
                @click="() => showControls = !showControls"
                title="Ferramentas"
                aria-label="Ferramentas"
                :icon="h(ToolOutlined)"
            />

            <transition name="fade" style="display: flex">
                <div v-if="showControls" class="search-type-overlay">
                    <a-button
                        class="map-icon-btn interactionBtn"
                        @click="() => isCoordsOpen = !isCoordsOpen"
                        title="Ver Coordenadas"
                        aria-label="Ver Coordenadas"
                        :icon="h(InfoCircleOutlined)"
                    />
                    <button class="map-icon-btn interactionBtn" title="Medir Distância" ref="measureLineBtn" @click="measureLine">
                        <i class="fas fa-ruler-horizontal"></i>
                    </button>
                    <button class="map-icon-btn interactionBtn" title="Medir Área" ref="measurePolygonBtn" @click="measurePolygon">
                        <i class="fas fa-draw-polygon"></i>
                    </button>
                    <a-button class="map-icon-btn interactionBtn" title="Limpar Medidas" @click="removeInteraction">
                        <i class="fas fa-eraser"></i>
                    </a-button>
                    <a-button class="map-icon-btn interactionBtn" title="Tela Cheia" @click="fullScreenMap">
                        <i class="fas fa-expand"></i>
                    </a-button>
                    <a-button class="map-icon-btn interactionBtn" title="Zoom In" @click="zoom('in')">
                        <i class="fas fa-search-plus"></i>
                    </a-button>
                    <a-button class="map-icon-btn interactionBtn" title="Zoom Out" @click="zoom('out')">
                        <i class="fas fa-search-minus"></i>
                    </a-button>
                    <a-button class="map-icon-btn interactionBtn" title="Rosa dos Ventos" @click="showCompassRose">
                        <i class="fas fa-compass"></i>
                    </a-button>
                    <a-button class="map-icon-btn interactionBtn" title="Resetar posição (Norte)" @click="rotateMap(0)">
                        <i class="fa-solid fa-rotate-left" style="transform: rotate(90deg)"></i>
                    </a-button>
                    <a-button
                        v-if="swipePermission"
                        class="map-icon-btn interactionBtn"
                        title="Abrir Layer Swipe"
                        aria-label="Abrir Layer Swipe"
                        @click="openSwipe"
                    >
                        <template #icon>
                            <ColumnWidthOutlined class="menu-icon" />
                        </template>
                    </a-button>
                    <a-button
                        class="map-icon-btn interactionBtn"
                        :class="{ 'layer-toggle-active': documentsLayerVisible }"
                        title="Exibir/Ocultar Documentos"
                        aria-label="Exibir/Ocultar Documentos"
                        :icon="h(FileTextOutlined)"
                        @click="toggleLayerVisibility('Documentos')"
                    />
                    <a-button
                        class="map-icon-btn interactionBtn"
                        :class="{ 'layer-toggle-active': imagesLayerVisible }"
                        title="Exibir/Ocultar Imagens"
                        aria-label="Exibir/Ocultar Imagens"
                        :icon="h(FileImageOutlined)"
                        @click="toggleLayerVisibility('Imagens')"
                    />
                </div>
            </transition>
        </div>
    </div>

    <div class="centered" ref="compassRose">
        <div id="north-arrow" ref="northArrow">
            <img
                src="/img/rosaventos.png"
                alt="Rosa dos Ventos"
                style="width: 100%">
        </div>
    </div>
</template>

<script setup>
import {h, onMounted, ref, watch} from "vue";
import {ColumnWidthOutlined, FileImageOutlined, FileTextOutlined, InfoCircleOutlined, ToolOutlined} from "@ant-design/icons-vue";
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import {Circle} from "ol/style";
import {Overlay} from "ol";
import {Draw} from "ol/interaction";
import {LineString, Polygon} from "ol/geom";
import {unByKey} from "ol/Observable";
import {toLonLat, transform} from "ol/proj";
import {getArea, getLength} from "ol/sphere";

const props = defineProps({
    map: { type: Object, required: true },
    swipePermission: { type: Boolean, default: false },
})
const emit = defineEmits(['draw-start', 'draw-end']);

const openSwipe = () => {
    window.open('/maps/swipe', '_blank', 'noopener,noreferrer')
}

const isCoordsOpen = ref(false)
const pos = ref({lon: '', lat: ''})
const utm = ref({zone: '', easting: '', northing: ''})
const viewProjection = props.map.getView().getProjection().getCode();

const showControls = ref(false)
const documentsLayerVisible = ref(false)
const imagesLayerVisible = ref(false)
const measureLineBtn = ref('')
const measurePolygonBtn = ref('')
let drawing = false
let helpTooltipElement
let helpTooltip
let sketch
let staticMeasureTooltips = []
let measureSource = new VectorSource();
let measureLayer = new VectorLayer({
    source: measureSource,
    title: "Medidas e áreas",
    displayInLayerSwitcher: false,
    visible: true,
    style: new Style({
        fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
        stroke: new Stroke({color: '#ffcc33', width: 2}),
        image: new Circle({radius: 7, fill: new Fill({color: '#ffcc33'})})
    }),
    zIndex: 9999,
});

const northArrow = ref('')
const compassRose = ref('')
const isDraggingArrow = ref(false)
const initialMapRotation = ref(0)
const initialMouseAngle = ref(0)

const showCompassRose = () => {
    (compassRose.value.style.display === 'block') ? compassRose.value.style.display = 'none' : compassRose.value.style.display = 'block'
}

const syncFileLayerVisibility = () => {
    const layers = props.map.getLayers().getArray()
    const documentsLayer = layers.find(layer => layer.get('name') === 'Documentos')
    const imagesLayer = layers.find(layer => layer.get('name') === 'Imagens')

    documentsLayerVisible.value = !!documentsLayer?.getVisible()
    imagesLayerVisible.value = !!imagesLayer?.getVisible()
}

const toggleLayerVisibility = (layerName) => {
    const layers = props.map.getLayers().getArray()
    const layer = layers.find(item => item.get('name') === layerName)
    if (!layer) return

    layer.setVisible(!layer.getVisible())
    syncFileLayerVisibility()
}

onMounted(() => {
    syncFileLayerVisibility()

    if (!!northArrow.value) {
        const view = props.map.getView();

        view.on('change:rotation', function () {
            const rotation = view.getRotation();
            northArrow.value.style.transform = `rotate(${rotation * (180 / Math.PI)}deg)`;
            northArrow.value.style.cursor = 'grabbing';
        });

        northArrow.value.addEventListener('mousedown', function (e) {
            e.preventDefault();
            e.stopPropagation();
            isDraggingArrow.value = true;
            initialMapRotation.value = view.getRotation();

            const rect = northArrow.value.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            initialMouseAngle.value = Math.atan2(e.clientY - centerY, e.clientX - centerX);

            document.addEventListener('mousemove', onMouseMoveArrow);
            document.addEventListener('mouseup', onMouseUpArrow);
        });

        const onMouseMoveArrow = function (e) {
            if (!isDraggingArrow) return;

            const rect = northArrow.value.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            const currentMouseAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX);

            let angleDelta = currentMouseAngle - initialMouseAngle.value;
            view.setRotation(initialMapRotation.value + angleDelta);
        };

        const onMouseUpArrow = function () {
            isDraggingArrow.value = false;
            northArrow.value.style.cursor = 'grab';
            document.removeEventListener('mousemove', onMouseMoveArrow);
            document.removeEventListener('mouseup', onMouseUpArrow);
        };
    }
})

const createHelpTooltip = () => {
    if (helpTooltipElement) {
        helpTooltipElement.parentNode.removeChild(helpTooltipElement);
    }
    helpTooltipElement = document.createElement('div');
    helpTooltipElement.className = 'ol-tooltip hidden';
    helpTooltip = new Overlay({
        element: helpTooltipElement,
        offset: [15, 0],
        positioning: 'center-left',
        stopEvent: false
    });
    props.map.addOverlay(helpTooltip);
}

const removeInteraction = () => {
    if (drawing) {
        props.map.getInteractions().forEach(interaction => {
            if (interaction instanceof Draw) {
                interaction.finishDrawing();
                props.map.removeInteraction(interaction);
            }
        });
        drawing = false;
    }

    if (!!helpTooltipElement) {
        helpTooltipElement.parentNode.removeChild(helpTooltipElement);
        helpTooltipElement = null;
        helpTooltip = null;
    }
    props.map.getOverlays().clear()
    staticMeasureTooltips.forEach((staticMeasureTooltip) => {
        props.map.removeOverlay(staticMeasureTooltip);
    })
    staticMeasureTooltips = [];

    measureSource.clear();

    document.querySelectorAll('.interactionBtn').forEach(btn => {
        btn.classList.remove('active');
    });
}

const addInteraction = (type) => {
    removeInteraction();

    if (type === 'LineString' || type === 'Polygon') {
        const draw = new Draw({
            source: measureSource,
            type: type,
            style: new Style({
                fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
                stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.5)', lineDash: [10, 10], width: 2 }),
                image: new Circle({
                    radius: 5,
                    stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.7)' }),
                    fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
                }),
            }),
        });
        props.map.addInteraction(draw);
        drawing = true

        createHelpTooltip();

        let listener;
        let temporaryMeasureTooltipElement;
        let temporaryMeasureTooltip;

        draw.on('drawstart', function(evt) {
            emit('draw-start')
            sketch = evt.feature;

            temporaryMeasureTooltipElement = document.createElement('div');
            temporaryMeasureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
            temporaryMeasureTooltip = new Overlay({
                element: temporaryMeasureTooltipElement,
                offset: [0, -15],
                positioning: 'bottom-center',
                stopEvent: false,
                insertFirst: false,
            });
            props.map.addOverlay(temporaryMeasureTooltip);

            let tooltipCoord = evt.coordinate;

            listener = sketch.getGeometry().on('change', function(evt) {
                const geom = evt.target;
                let output;
                if (geom instanceof Polygon) {
                    output = formatArea(geom);

                    tooltipCoord = geom.getInteriorPoint().getCoordinates();
                } else if (geom instanceof LineString) {
                    output = formatLength(geom);

                    tooltipCoord = geom.getLastCoordinate();
                }
                temporaryMeasureTooltipElement.innerHTML = output;
                temporaryMeasureTooltip.setPosition(tooltipCoord);
            });
        });

        draw.on('drawend', function() {
            temporaryMeasureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
            temporaryMeasureTooltip.setOffset([0, -7]);
            staticMeasureTooltips.push(temporaryMeasureTooltip);

            sketch = null;
            unByKey(listener);

            temporaryMeasureTooltipElement = null;
            temporaryMeasureTooltip = null;

            emit('draw-end')
        });

        const buttonId = (type === 'LineString') ? measureLineBtn.value : measurePolygonBtn.value;
        buttonId.classList.add('active');
    }
}

const zoom = (type) => {
    const view = props.map.getView();
    const currentZoom = view.getZoom();
    view.animate({
        zoom: type === 'in' ? currentZoom + 1 : currentZoom - 1,
        duration: 250
    });
}

const measureLine = () => {
    if (measureLineBtn.value.classList.contains('active')) {
        removeInteraction();
    } else {
        addInteraction('LineString');
    }
}

const measurePolygon = () => {
    if (measurePolygonBtn.value.classList.contains('active')) {
        removeInteraction();
    } else {
        addInteraction('Polygon');
    }
}

const formatLength = (line) => {
    const geom4326 =
        viewProjection === 'EPSG:4326'
            ? line
            : line.clone().transform(viewProjection, 'EPSG:4326');

    const length = getLength(geom4326, { projection: 'EPSG:4326' });

    return length > 1000
        ? (length / 1000).toFixed(2) + ' km'
        : length.toFixed(2) + ' m';
};

const formatArea = (polygon) => {
    const geom4326 =
        viewProjection === 'EPSG:4326'
            ? polygon
            : polygon.clone().transform(viewProjection, 'EPSG:4326');

    const area = getArea(geom4326, { projection: 'EPSG:4326' });

    return area > 10000
        ? `${(area / 1e6).toFixed(2)} km<sup>2</sup>`
        : `${area.toFixed(2)} m<sup>2</sup>`;
};

const fullScreenMap = () => props.map.getTargetElement().requestFullscreen({ navigationUI: "hide" });

props.map.on('pointermove', function (evt) {
    if (evt.dragging) return

    const coordinate = evt.coordinate;

    let lonLat;
    if (viewProjection === 'EPSG:4326') {
        lonLat = coordinate;
    } else {
        lonLat = toLonLat(coordinate);
    }

    pos.value = {lon: lonLat[0].toFixed(6), lat: lonLat[1].toFixed(6)}

    const lonForUTM = lonLat[0];
    let utmObj = {};

    if (lonForUTM >= -66 && lonForUTM < -60) {
        utmObj.zone = '21S';
    } else if (lonForUTM >= -60 && lonForUTM < -54) {
        utmObj.zone = '22S';
    } else if (lonForUTM >= -54 && lonForUTM < -48) {
        utmObj.zone = '23S';
    } else if (lonForUTM >= -48 && lonForUTM < -42) {
        utmObj.zone = '24S';
    } else {
        utmObj.zone = '23S';
    }

    const utmProjection = `EPSG:327${utmObj.zone.substring(0, 2)}`;
    const utmCoordinate = transform(coordinate, viewProjection, utmProjection);
    utmObj.easting = utmCoordinate[0].toFixed(2);
    utmObj.northing = utmCoordinate[1].toFixed(2);
    utm.value = utmObj
});

function rotateMap(angle) {
    const view = props.map.getView();
    view.animate({
        rotation: angle,
        duration: 500
    });
}

watch(
    () => props.map,
    (map) => {
        if (!map) return;
        props.map.addLayer(measureLayer);
        syncFileLayerVisibility()
    },
    { immediate: true }
);
</script>

<style scoped>
.map-icons {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.map-icon-btn {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, .6);
    color: white;
    border: 2px solid white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.map-icon-btn:hover {
    background-color: rgba(0, 60, 136, .7);
}

.menu-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.map-icon-btn i {
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.layer-toggle-active {
    background-color: rgba(22, 119, 255, 0.95);
}

.coordinate-display {
    background: #00000090;
    color: #FFFFFF;
    padding: 5px 10px;
    border-radius: 5px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    z-index: 1000;
}

#north-arrow {
    width: 100%;
    border-radius: 50%;
    overflow: hidden;
}

.centered {
    width: 30%;
    display: none;
    position: fixed;
    user-select: none;
    z-index: 1001;
    transform-origin: center center;
    cursor: grab;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
}
</style>